import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Users, X, Copy, LogOut, UserPlus, Radio, Check, MousePointer2, Hand, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useCollab, colorForUser, initialsFor } from '@/contexts/CollabContext';
import { getPresence } from '@/lib/messagingSocket';
import MirrorView from '@/components/collab/MirrorView';

function InitialBadge({ id, name, size = 26, ring = false }) {
  const color = colorForUser(id);
  return (
    <div
      title={name}
      style={{ width: size, height: size, background: color, boxShadow: ring ? `0 0 0 2px rgba(255,255,255,0.9)` : 'none' }}
      className="rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
    >
      {initialsFor(name)}
    </div>
  );
}

function RemoteCursor({ c }) {
  const left = c.x * window.innerWidth;
  const top = c.y * window.innerHeight;
  return (
    <div className="fixed pointer-events-none z-[9998]" style={{ left, top, transform: 'translate(-2px,-2px)', transition: 'left 60ms linear, top 60ms linear' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M5 3l14 7-6 2-2 6-6-15z" fill={c.color} stroke="white" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
      <div className="mt-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-white whitespace-nowrap shadow" style={{ background: c.color }}>
        {c.name || 'Guest'}
      </div>
    </div>
  );
}

function InviteDialog({ onClose }) {
  const { inviteUsers, joinLink, session, currentUserId } = useCollab();
  const [picked, setPicked] = useState(new Set());

  const teammates = useMemo(() => {
    const pres = getPresence() || {};
    const memberIds = new Set((session?.members || []).map((m) => m.user_id));
    return Object.entries(pres)
      .filter(([uid]) => uid !== currentUserId && !memberIds.has(uid))
      .map(([uid, v]) => ({ uid, name: v.name || 'Teammate', status: v.status }));
  }, [session, currentUserId]);

  const toggle = (uid) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(uid)) n.delete(uid); else if (n.size < 5) n.add(uid);
    return n;
  });

  const send = () => {
    inviteUsers(Array.from(picked));
    toast.success(`Invited ${picked.size} teammate${picked.size === 1 ? '' : 's'}`);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-[380px] max-w-[92vw] rounded-2xl bg-background border border-border/60 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="collab-invite-dialog">
        <div className="p-4 border-b border-border/40 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><UserPlus className="w-4 h-4 text-primary" /> Invite teammates</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="collab-invite-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[11px] text-muted-foreground">Pick up to 5 teammates who are online. They'll get a live invite to join your screen.</p>
          <div className="max-h-56 overflow-auto space-y-1">
            {teammates.length === 0 && <p className="text-xs text-muted-foreground/70 py-6 text-center">No teammates online right now. Share the join link below instead.</p>}
            {teammates.map((t) => (
              <button key={t.uid} onClick={() => toggle(t.uid)} data-testid={`collab-invitee-${t.uid}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${picked.has(t.uid) ? 'bg-primary/15' : 'hover:bg-muted/40'}`}>
                <InitialBadge id={t.uid} name={t.name} />
                <span className="flex-1 text-xs text-foreground truncate">{t.name}</span>
                {picked.has(t.uid) && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
          <button onClick={() => { navigator.clipboard?.writeText(joinLink); toast.success('Join link copied'); }}
            className="w-full flex items-center justify-center gap-1.5 h-8 rounded-lg border border-border/50 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors" data-testid="collab-copy-link">
            <Copy className="w-3.5 h-3.5" /> Copy join link
          </button>
          <button onClick={send} disabled={picked.size === 0}
            className="w-full h-9 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 transition-colors" data-testid="collab-invite-send">
            Invite {picked.size > 0 ? `(${picked.size})` : ''}
          </button>
        </div>
      </div>
    </div>, document.body);
}

function ControlMenu() {
  const { session, controlledByGuest, grantControl, revokeControl } = useCollab();
  const [open, setOpen] = useState(false);
  const guests = (session?.members || []).filter((m) => m.user_id !== session.hostId);
  const controller = (session?.members || []).find((m) => m.user_id === session.controllerId);

  if (controlledByGuest) {
    return (
      <button onClick={revokeControl} className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-amber-500/15 text-amber-400 text-[11px] font-medium hover:bg-amber-500/25 transition-colors" data-testid="collab-takeback-btn" title={`${controller?.name || 'A guest'} has control`}>
        <RotateCcw className="w-3.5 h-3.5" /> Take back
      </button>
    );
  }
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} disabled={guests.length === 0} className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-muted/50 text-foreground text-[11px] font-medium hover:bg-muted disabled:opacity-40 transition-colors" data-testid="collab-give-control-btn">
        <Hand className="w-3.5 h-3.5" /> Give control
      </button>
      {open && guests.length > 0 && (
        <div className="absolute bottom-9 left-0 w-52 rounded-xl bg-background border border-border/60 shadow-2xl p-1.5" data-testid="collab-control-menu">
          <p className="text-[10px] text-muted-foreground px-2 py-1">Hand control to one guest. Delete / publish / promote stay host-only.</p>
          {guests.map((g) => (
            <button key={g.user_id} onClick={() => { grantControl(g.user_id); setOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-muted/40 transition-colors" data-testid={`collab-grant-${g.user_id}`}>
              <InitialBadge id={g.user_id} name={g.name} size={22} />
              <span className="flex-1 text-xs text-foreground truncate">{g.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CollabOverlay() {
  const { session, cursors, isHost, isController, amController, currentUserId, endSession, invite, acceptInvite, dismissInvite } = useCollab();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <>
      {/* Guest live mirror of the host's screen */}
      <MirrorView />

      {/* Incoming invite prompt */}
      {invite && createPortal(
        <div className="fixed bottom-5 right-5 z-[10000] w-72 rounded-2xl bg-background border border-primary/40 shadow-2xl p-4" data-testid="collab-invite-prompt">
          <div className="flex items-center gap-2 mb-1"><Radio className="w-4 h-4 text-primary animate-pulse" /><span className="text-sm font-semibold text-foreground">Live session invite</span></div>
          <p className="text-[11px] text-muted-foreground mb-3"><span className="font-medium text-foreground">{invite.hostName || 'A teammate'}</span> invited you to view their screen live.</p>
          <div className="flex gap-2">
            <button onClick={acceptInvite} className="flex-1 h-8 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors" data-testid="collab-invite-accept">Join</button>
            <button onClick={dismissInvite} className="px-3 h-8 rounded-lg border border-border/50 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="collab-invite-dismiss">Dismiss</button>
          </div>
        </div>, document.body)}

      {!session ? null : (
        <>
          {/* Live cursors — shown on the host's screen (guests see them on
              the mirror overlay instead). */}
          {isHost && createPortal(
            <div aria-hidden data-collab-norecord className="collab-norecord">
              {Object.entries(cursors).filter(([uid]) => uid !== currentUserId).map(([uid, c]) => <RemoteCursor key={uid} c={c} />)}
            </div>, document.body)}

          {/* Presence bar */}
          {createPortal(
            <div data-collab-norecord className="collab-norecord fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-3 py-2 rounded-full bg-background/95 backdrop-blur-xl border border-border/60 shadow-2xl" data-testid="collab-presence-bar" data-session-id={session.sessionId}>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[11px] font-semibold text-foreground">Live{isHost ? ' · Host' : ''}</span>
              </div>
              <div className="flex items-center -space-x-1.5">
                {(session.members || []).map((m) => (
                  <InitialBadge key={m.user_id} id={m.user_id} name={m.name} ring={m.user_id === session.hostId} />
                ))}
              </div>
              <span className="text-[10px] text-muted-foreground">{(session.members || []).length}/6</span>
              {isHost && <ControlMenu />}
              {!isHost && amController && (
                <span className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-medium" data-testid="collab-has-control">
                  <MousePointer2 className="w-3.5 h-3.5" /> You're in control
                </span>
              )}
              {isHost && (
                <button onClick={() => setInviteOpen(true)} className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-primary/15 text-primary text-[11px] font-medium hover:bg-primary/25 transition-colors" data-testid="collab-invite-btn">
                  <UserPlus className="w-3.5 h-3.5" /> Invite
                </button>
              )}
              <button onClick={endSession} className="flex items-center gap-1 h-7 px-2.5 rounded-full bg-destructive/15 text-destructive text-[11px] font-medium hover:bg-destructive/25 transition-colors" data-testid="collab-end-btn">
                <LogOut className="w-3.5 h-3.5" /> {isHost ? 'End' : 'Leave'}
              </button>
            </div>, document.body)}

          {inviteOpen && <InviteDialog onClose={() => setInviteOpen(false)} />}
        </>
      )}
    </>
  );
}
